import React from 'react';
import { Box, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import Badge from 'src/components/Badge';
import { saveDataToStorage } from 'src/utils';
import { useStoreUnitActions } from 'src/redux/helpers';
import { useModal } from 'src/hooks';
import { isModalComplete } from 'src/components/ModalDialog';
import {
  DeploymentStatus,
  SolutionEdgeWorkloadItem,
  SolutionEdgeWorkloadRepositoryType,
  SolutionEdgeWorkloadStatus as Status,
} from '../../types/solutionEdgeWorkload';
import SolutionWorkloadStatusViewModal from './SolutionWorkloadStatusViewModal';
import solutionsStore from '../../redux';

type Props = {
  value?: string;
  item?: SolutionEdgeWorkloadItem;
};

const buttonStyle = {
  textDecoration: 'underline',
  color: '#0047BA',
  cursor: 'pointer',
  fontWeight: 500,
  paddingLeft: '3px',
  fontSize: '13px',
};

function getDeploymentBadge(
  deploymentPaused: boolean,
  deploymentStatus: string,
  activeCount: number,
  totalCount: number,
  deploymentSummary: string
): { color: 'success' | 'warning' | 'error' | 'default' | 'info'; label: string } {
  if (deploymentPaused) {
    return { color: 'info', label: deploymentStatus || 'Paused' };
  }
  if (totalCount > 0 && activeCount === totalCount) {
    return { color: 'success', label: `${deploymentSummary} Active` };
  }
  if (totalCount > activeCount && activeCount > 0) {
    return { color: 'warning', label: `${deploymentSummary} Error` };
  }
  if (totalCount > 0 && activeCount === 0) {
    return { color: 'error', label: `${deploymentSummary} Failed` };
  }
  return { color: 'default', label: deploymentSummary || '-' };
}

function SolutionEdgeWorkloadClusterStatus({ value = '', item }: Props) {
  let statusData: any = {};
  try {
    statusData = value ? JSON.parse(value) : {};
  } catch {
    statusData = {};
  }
  const deploymentSummary: string = statusData?.deploymentSummary ?? '';
  const activeDeploymentCount: number = statusData?.activeDeploymentCount ?? 0;
  const totalDeploymentCount: number = statusData?.totalDeploymentCount ?? 0;
  const deploymentPaused: boolean = statusData?.deploymentPaused ?? false;
  const errorMsg: string = statusData?.errorMsg ?? '';
  const tokenExpiryRemainingDays = item?.tokenExpiryRemainingDays;
  const shouldShowTokenExpiryWarning = typeof tokenExpiryRemainingDays === 'number';
  const tokenExpiryMessage =
    tokenExpiryRemainingDays !== undefined && tokenExpiryRemainingDays < 0
      ? `Token expired ${Math.abs(tokenExpiryRemainingDays)} days ago`
      : `Token expiring in ${tokenExpiryRemainingDays} days`;
  const tokenExpiryColor =
    tokenExpiryRemainingDays !== undefined && tokenExpiryRemainingDays < 30
      ? 'error.main'
      : 'warning.main';
  const statusValue: Status = statusData?.status
    ? statusData.status.toLowerCase()
    : '';

  const { solutionId } = useParams();
  const actions = useStoreUnitActions(solutionsStore);

  const onClose = (reason) =>
    isModalComplete(reason) && actions.subscriptions(solutionId);

  const handleClick = (event: any) => {
    event.stopPropagation();
    saveDataToStorage('DeploymentStatus', DeploymentStatus.Conditions);
    showWorkloadStatusDialog();
  };

  const [workloadStatusDialog, showWorkloadStatusDialog] = useModal(
    SolutionWorkloadStatusViewModal,
    { item, onClose }
  );

  const badge = getDeploymentBadge(
    deploymentPaused,
    statusData?.status || '',
    activeDeploymentCount,
    totalDeploymentCount,
    deploymentSummary
  );

  return (
    <Box>
      <Badge color={badge.color} label={badge.label} />
      {shouldShowTokenExpiryWarning && (
        <Typography
          sx={{
            display: '-webkit-box',
            color: tokenExpiryColor,
            fontSize: '13px',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            textOverflow: 'ellipsis',
            wordBreak: 'break-all',
            overflow: 'hidden',
            whiteSpace: 'normal',
          }}
        >
          {tokenExpiryMessage}
        </Typography>
      )}
      {item?.gitFlag === SolutionEdgeWorkloadRepositoryType.SolutionEdge &&
        (statusValue === Status.notReady || errorMsg) && (
          <Typography
            variant="small2"
            onClick={handleClick}
            style={{ ...buttonStyle, display: 'block' }}
          >
            Click here for more
          </Typography>
        )}
      {workloadStatusDialog}
    </Box>
  );
}

export default SolutionEdgeWorkloadClusterStatus;
